import { Box, CircularProgress, Stack, Typography } from '@ace-clublabs/shared-component-library';
import { numberHelper } from '../../utils/numberHelper';
import { MembershipAppStateContext } from '../MembershipAppState/MembershipAppState';
import { useContext } from 'react';

interface PriceInfoCardProps {
  originalPrice?: number;
  priceAfterDiscount?: number;
  priceRange?: { min: number; max: number };
  priceSuffix?: string;
  className?: string;
}

export const PriceInfoCard = ({
  originalPrice,
  priceAfterDiscount,
  priceSuffix,
  priceRange,
  className,
}: PriceInfoCardProps) => {
  const isFree = priceAfterDiscount === 0;
  const hasDiscount =
    priceAfterDiscount !== undefined && originalPrice !== undefined && priceAfterDiscount < originalPrice;
  const { isPriceCartLoading } = useContext(MembershipAppStateContext);
  return isPriceCartLoading ? (
    <Stack className="price-spinner" direction="row" alignItems="center" sx={{ gap: '0.5rem' }}>
      <Box marginTop="0.5rem">
        <CircularProgress
          variant="indeterminate"
          color="inherit"
          size={20}
          sx={{ color: '#016BCD', lineHeight: '1.75rem' }}
        />
      </Box>
      <Typography
        variant="b2Regular"
        className={`recurring-period-label-${className}`}
        sx={{
          color: '#6C7175',
        }}
      >
        /{priceSuffix}
      </Typography>
    </Stack>
  ) : (
    <>
      <Stack direction="row" alignSelf="flex-start" alignItems="baseline" sx={{ gap: '0.5rem', lineHeight: '1.75rem' }}>
        {isFree && (
          <Typography
            variant="b1Medium"
            className={`free-price-label-${className}`}
            sx={{ whiteSpace: 'nowrap', color: '#7527B9' }}
          >
            {className === 'family-upgrade-fee' ? 'Waived' : 'Free'}
          </Typography>
        )}
        {!isFree && hasDiscount && priceAfterDiscount !== undefined && (
          <Typography
            sx={{ display: 'flex', alignItems: 'center' }}
            className={`discounted-price-${className}`}
            variant="b1Medium"
          >
            {numberHelper.formatMoney(priceAfterDiscount)}
          </Typography>
        )}
        {/*This item is for displaying the price range for associates costs*/}
        {priceRange !== undefined && (
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              ...(hasDiscount && { color: '#6C7175', textDecoration: 'line-through' }),
            }}
            className={`original-cost-${className}`}
            variant={hasDiscount ? 'b2Regular' : 'b1Medium'}
          >
            {numberHelper.formatMoney(priceRange.min)}-{numberHelper.formatMoney(priceRange.max, true)}
            <Typography
              variant="b2Regular"
              className={`recurring-period-label-${className}`}
              sx={{
                whiteSpace: 'nowrap',
                textDecoration: 'inherit',
                color: '#6C7175',
                marginLeft: hasDiscount ? 0 : '0.25rem',
              }}
            >
              /{priceSuffix}
            </Typography>
          </Typography>
        )}
        {originalPrice !== undefined && (
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              ...(hasDiscount && { color: '#6C7175', textDecoration: 'line-through' }),
            }}
            className={`original-cost-${className}`}
            variant={hasDiscount ? 'b2Regular' : 'b1Medium'}
          >
            {numberHelper.formatMoney(originalPrice)}
            {priceSuffix && (
              <Typography
                variant="b2Regular"
                className={`recurring-period-label-${className}`}
                sx={{
                  whiteSpace: 'nowrap',
                  textDecoration: 'inherit',
                  color: '#6C7175',
                  marginLeft: hasDiscount ? 0 : '0.25rem',
                }}
              >
                /{priceSuffix}
              </Typography>
            )}
          </Typography>
        )}
      </Stack>
    </>
  );
};
